import PaymentEntity from 'src/db/entities/payment/PaymentEntity';

/** Newest received date first; ties keep the newest created payment on top. */
export const sortPaymentsByReceivedDate = (payments: PaymentEntity[]): PaymentEntity[] =>
  [...payments].sort(
    (a, b) => b.receivedAt - a.receivedAt || (b.createdAt ?? 0) - (a.createdAt ?? 0)
  );

export const filterPaymentsByInvoice = (
  payments: PaymentEntity[],
  invoiceId: string
): PaymentEntity[] => payments.filter((payment) => payment.proformaInvoiceId === invoiceId);

export type BalanceKind = 'due' | 'overpaid' | 'settled';

export interface BalanceView {
  kind: BalanceKind;
  // Always positive.
  amount: number;
}

/**
 * How to show the server's `balance_due`: a positive one is still owed, a
 * negative one means the invoice was overpaid. Cents are compared, so float
 * noise such as 0.0000001 does not read as a debt. Null (no invoice total) is
 * shown as settled.
 */
export const getBalanceView = (balanceDue: number | null | undefined): BalanceView => {
  const cents = Math.round((balanceDue ?? 0) * 100);

  if (cents > 0) {
    return { kind: 'due', amount: cents / 100 };
  }
  if (cents < 0) {
    return { kind: 'overpaid', amount: -cents / 100 };
  }

  return { kind: 'settled', amount: 0 };
};

/** Parses an amount typed by the user; null when it is not a positive number. */
export const parsePaymentAmount = (input: string): number | null => {
  const normalized = input.trim().replace(',', '.');
  if (!normalized) {
    return null;
  }

  const value = Number(normalized);
  if (!Number.isFinite(value)) {
    return null;
  }

  const rounded = Math.round(value * 100) / 100;

  return rounded > 0 ? rounded : null;
};

const pad = (value: number) => String(value).padStart(2, '0');

/** `yyyy-MM-dd` of the local calendar day, the value of an `<input type="date">`. */
export const toDateInputValue = (timestamp: number): string => {
  const date = new Date(timestamp);

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/**
 * Timestamp for a picked day: local noon, so the day survives the whole-second
 * conversion and a timezone shift of a few hours in either direction.
 */
export const fromDateInputValue = (value: string): number | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }

  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day), 12);

  return Number.isNaN(date.getTime()) ? null : date.getTime();
};
